import React from 'react';
import { CommissionDistributionFlow } from './CommissionDistributionFlow';
import { COMMISSION_FLOW_TERMS } from '../../lib/commissionFlowModel';
import { formatMoney, formatPlacedDate } from '../../lib/format';
import { Card, CardBody } from '../PageChrome';

/**
 * @param {{ flow: object, orderLabel?: string, placedAt?: string, periodLabel?: string, showPeriodInTerms?: boolean, subtitlePrefix?: string, sectionTitle?: string, description?: string }} props
 */
export function CommissionOrderFlowSection({
  flow,
  orderLabel,
  placedAt,
  paymentMethod,
  periodLabel,
  showPeriodInTerms = false,
  subtitlePrefix,
  sectionTitle = 'Order commission distribution',
  description,
}) {
  const isCod = String(paymentMethod ?? flow?.paymentMethod ?? '')
    .trim()
    .toUpperCase() === 'COD';
  return (
    <Card className="mb-0 border-0 shadow-sm analytics-flow-card">
      <CardBody className="p-3 p-md-4">
        <div className="analytics-flow-card__head d-flex flex-column flex-xl-row align-items-xl-start justify-content-between gap-3 gap-xl-4 mb-3 mb-xl-4">
          <div className="min-w-0">
            <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-1">{sectionTitle}</h2>
            <p className="small text-secondary mb-0">
              {description ? (
                description
              ) : orderLabel ? (
                <>
                  {subtitlePrefix ? `${subtitlePrefix} — ` : null}
                  {subtitlePrefix ? null : 'Order '}
                  <span className="fw-semibold text-dark">{orderLabel}</span>
                  {placedAt ? <span className="ms-1">· {formatPlacedDate(placedAt)}</span> : null}
                </>
              ) : (
                'How this order splits between café, platform, and PG fees'
              )}
              {isCod ? (
                <span className="badge rounded-pill text-bg-light border ms-0 ms-sm-2 mt-2 mt-sm-0 d-inline-block">
                  Cash on delivery
                </span>
              ) : null}
            </p>
          </div>
          <aside className="analytics-flow-terms flex-shrink-0 w-100 w-xl-auto">
            <h3 className="analytics-flow-terms__title small text-secondary text-uppercase letter-spacing-tight mb-2">
              Agreed terms
            </h3>
            <dl className="analytics-flow-terms__grid small mb-0">
              <div className="analytics-flow-terms__item">
                <dt>Partner café</dt>
                <dd>{COMMISSION_FLOW_TERMS.partnerName}</dd>
              </div>
              <div className="analytics-flow-terms__item">
                <dt>Platform commission</dt>
                <dd>
                  {COMMISSION_FLOW_TERMS.platformCommissionPct}% — {COMMISSION_FLOW_TERMS.platformCommissionNote}
                </dd>
              </div>
              <div className="analytics-flow-terms__item">
                <dt>PG — platform bears</dt>
                <dd>
                  {isCod ? (
                    COMMISSION_FLOW_TERMS.codPgNote
                  ) : (
                    <>
                      {COMMISSION_FLOW_TERMS.pgRatePct}% — {COMMISSION_FLOW_TERMS.pgPlatformNote}
                    </>
                  )}
                </dd>
              </div>
              <div className="analytics-flow-terms__item">
                <dt>PG — café bears</dt>
                <dd>{isCod ? '₹0 (COD)' : COMMISSION_FLOW_TERMS.pgCafeNote}</dd>
              </div>
              <div className="analytics-flow-terms__item">
                <dt>Settlement</dt>
                <dd>{COMMISSION_FLOW_TERMS.settlement}</dd>
              </div>
              {showPeriodInTerms && periodLabel ? (
                <div className="analytics-flow-terms__item">
                  <dt>Selected period</dt>
                  <dd>{periodLabel}</dd>
                </div>
              ) : null}
            </dl>
          </aside>
        </div>

        <CommissionDistributionFlow flow={flow} />

        <dl className="row small mb-0 mt-3 mt-xl-4 pt-3 border-top commission-order-meta">
          <dt className="col-6 col-lg-2 text-secondary">Café product</dt>
          <dd className="col-6 col-lg-2 text-lg-end tabular-nums">{formatMoney(flow.cafeProductBase)}</dd>
          <dt className="col-6 col-lg-2 text-secondary">Menu line</dt>
          <dd className="col-6 col-lg-2 text-lg-end tabular-nums">{formatMoney(flow.menuWithMarkup)}</dd>
          <dt className="col-6 col-lg-2 text-secondary">Items subtotal</dt>
          <dd className="col-6 col-lg-2 text-lg-end tabular-nums fw-semibold text-dark">
            {formatMoney(flow.itemsSubtotal)}
          </dd>
          <dt className="col-6 col-lg-2 text-secondary">Customer paid</dt>
          <dd className="col-6 col-lg-2 text-lg-end tabular-nums fw-semibold text-dark">
            {formatMoney(flow.customerPaid)}
          </dd>
          <dt className="col-6 col-lg-2 text-secondary">Commission (base)</dt>
          <dd className="col-6 col-lg-2 text-lg-end tabular-nums">{formatMoney(flow.platformCommissionOnBase)}</dd>
          <dt className="col-6 col-lg-2 text-secondary">Commission (add-ons)</dt>
          <dd className="col-6 col-lg-2 text-lg-end tabular-nums">{formatMoney(flow.platformCommissionOnAddons)}</dd>
          <dt className="col-6 col-lg-2 text-secondary">PG platform bears</dt>
          <dd className="col-6 col-lg-2 text-lg-end tabular-nums">{formatMoney(flow.pgPlatformBears)}</dd>
          <dt className="col-6 col-lg-2 text-secondary">PG café bears</dt>
          <dd className="col-6 col-lg-2 text-lg-end tabular-nums">{formatMoney(flow.pgCafeTotal)}</dd>
          <dt className="col-6 col-lg-2 text-secondary">Café net</dt>
          <dd className="col-6 col-lg-2 text-lg-end tabular-nums fw-semibold" style={{ color: '#2e7d32' }}>
            {formatMoney(flow.cafeNetSettlement)}
          </dd>
          <dt className="col-6 col-lg-2 text-secondary">Platform net</dt>
          <dd className="col-6 col-lg-2 text-lg-end tabular-nums fw-semibold" style={{ color: '#c55a1d' }}>
            {formatMoney(flow.platformNet)}
          </dd>
        </dl>
      </CardBody>
    </Card>
  );
}
